import { Button, Switch } from '@material-ui/core';
import { CADENA_VACIA } from 'utils/constants';

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import AnalyticsService from 'services/AnalyticsService';
import { actualizarSwitchCortesFuturos, RootState } from 'store';
import { EventosGtmSaesa } from 'utils/GtmEvents';
import { closePurple, flashSlashPurple, legendDots } from 'assets/images';
import { TECLA_ESCAPE } from 'components/Maps/OutageDashboard/outageDashboard.constants';
import { abrirEstoySinLuz } from 'components/Maps/OutageDashboard/outageDashboard.helpers';
import clsx from 'clsx';
import {
  TEST_ID_BOTON_REPORTAR_HEADER,
  TEST_ID_CERRAR_MODAL_REPORTAR,
  TEST_ID_CERRAR_SIMBOLOGIA,
  TEST_ID_CONFIRMAR_REPORTAR,
  TEST_ID_FAB_REPORTAR,
  TEST_ID_FAB_SIMBOLOGIA,
  TEST_ID_LOGO_HEADER,
  TEST_ID_LOGO_HEADER_SHEET,
  TEST_ID_MODAL_REPORTAR,
  TEST_ID_MODAL_REPORTAR_BACKDROP,
  TEST_ID_MODAL_REPORTAR_OVERLAY,
  TEST_ID_PANEL_SIMBOLOGIA,
  TEST_ID_SWITCH_FUTUROS,
  TEST_ID_SWITCH_FUTUROS_MOBILE,
} from './headerMapa.constants';
import useStyles from './HeaderMapaStyles';

/**
 * Header del mapa de cortes: logo, simbología y acceso a reportar corte.
 */
const HeaderMapa = () => {
  /**
   * Variable que maneja el uso de estilos.
   */
  const styles = useStyles();

  /**
   * Uso del traductor.
   */
  const { t } = useTranslation();

  /**
   * Despacha acciones al store.
   */
  const dispatch = useDispatch();

  /**
   * Logo de la distribuidora activa.
   */
  const logo = useSelector((state: RootState) => state.desconexion.distribuidora);

  /**
   * Indica si la distribuidora habilita los cortes futuros.
   */
  const cortesFuturosHabilitado = useSelector((state: RootState) => state.desconexion.cortesFuturos);

  /**
   * Indica si el usuario tiene activos los cortes futuros en el mapa.
   */
  const switchCortesFuturos = useSelector((state: RootState) => state.desconexion.switchCortesFuturos);

  /**
   * Indica si el detalle del corte está abierto y el header debe reducirse.
   */
  const modalDetalleAbierto = useSelector((state: RootState) => state.desconexion.modalMobileAbierto);

  /**
   * Estado que indica si el panel mobile de simbología está abierto.
   */
  const [panelMobileAbierto, setPanelMobileAbierto] = useState(false);

  /**
   * Estado que indica si la confirmación de reportar corte está abierta.
   */
  const [modalReportarAbierto, setModalReportarAbierto] = useState(false);

  /**
   * Referencia del botón que abre la simbología en mobile.
   */
  const fabSimbologiaRef = useRef<HTMLButtonElement>(null);

  /**
   * Con el detalle abierto se ocultan la simbología y reportar. En mobile el logo sigue visible.
   */
  const mostrarHeaderCompleto = !modalDetalleAbierto;

  /**
   * Cierra la simbología y abre la confirmación de reportar corte.
   */
  const abrirModalReportar = () => {
    setPanelMobileAbierto(false);
    setModalReportarAbierto(true);
  };

  /**
   * Cierra la confirmación de reportar corte.
   */
  const cerrarModalReportar = () => {
    setModalReportarAbierto(false);
  };

  /**
   * Confirma el reporte y abre Estoy sin luz.
   */
  const confirmarReportarCorte = () => {
    setModalReportarAbierto(false);
    abrirEstoySinLuz();
  };

  /**
   * Activa o desactiva la visualización de cortes futuros en el mapa.
   */
  const onSwitchCortesFuturosChange = () => {
    dispatch(actualizarSwitchCortesFuturos(!switchCortesFuturos));
    AnalyticsService.enviarTag(EventosGtmSaesa.mapaDesconexiones.poligono.switchCortesFuturos);
  };

  /**
   * Cierra la simbología y la confirmación de reporte al presionar Escape.
   */
  useEffect(() => {
    if (!panelMobileAbierto && !modalReportarAbierto) {
      return undefined;
    }

    /**
     * Cierra la simbología y la confirmación de reporte si la tecla es Escape.
     */
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === TECLA_ESCAPE) {
        setPanelMobileAbierto(false);
        setModalReportarAbierto(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [panelMobileAbierto, modalReportarAbierto]);

  /**
   * Cierra el panel mobile de simbología.
   */
  const cerrarPanelMobile = () => {
    setPanelMobileAbierto(false);
  };

  /**
   * Simbología de escritorio: tipos de corte y switch de cortes futuros.
   */
  const legendItems = (
    <>
      <div className={styles.legendGrupo}>
        <span className={styles.legendTitulo}>{t('desconexion:paginas.mapaCortes.tipoCortes.title')}</span>
        <div className={styles.legendItem}>
          <span className={clsx(styles.legendDot, styles.dotInesperado)} />
          <span className={styles.legendTexto}>{t('desconexion:paginas.mapaCortes.tipoCortes.item1')}</span>
        </div>
        <div className={styles.legendItem}>
          <span className={clsx(styles.legendDot, styles.dotProgramado)} />
          <span className={styles.legendTexto}>{t('desconexion:paginas.mapaCortes.tipoCortes.item2')}</span>
        </div>
      </div>
      {cortesFuturosHabilitado && (
        <>
          <div className={styles.separador} />
          <div className={styles.futurosCnt}>
            <div className={styles.legendItem}>
              <span className={clsx(styles.legendDot, styles.dotFuturos)} />
              <span className={clsx(styles.legendTexto, { [styles.legendTextoMuted]: !switchCortesFuturos })}>
                {t('desconexion:paginas.mapaCortes.tipoCortes.item3')}
              </span>
            </div>
            <Switch
              checked={switchCortesFuturos}
              onChange={onSwitchCortesFuturosChange}
              color="primary"
              inputProps={{ 'aria-label': 'switch-cortes-futuros' }}
              data-testid={TEST_ID_SWITCH_FUTUROS}
            />
          </div>
        </>
      )}
    </>
  );

  /**
   * Simbología mobile en barra horizontal, con el switch de cortes futuros.
   */
  const legendItemsMobile = (
    <div className={styles.barraLeyendaScroll}>
      <span className={styles.legendTitulo}>{t('desconexion:paginas.mapaCortes.tipoCortes.title')}</span>
      <div className={styles.legendItem}>
        <span className={clsx(styles.legendDot, styles.dotInesperado)} />
        <span className={styles.legendTexto}>{t('desconexion:paginas.mapaCortes.tipoCortes.item1')}</span>
      </div>
      <div className={styles.legendItem}>
        <span className={clsx(styles.legendDot, styles.dotProgramado)} />
        <span className={styles.legendTexto}>{t('desconexion:paginas.mapaCortes.tipoCortes.item2')}</span>
      </div>
      {cortesFuturosHabilitado && (
        <div className={styles.futurosCnt}>
          <div className={styles.legendItem}>
            <span className={clsx(styles.legendDot, styles.dotFuturos)} />
            <span className={clsx(styles.legendTexto, { [styles.legendTextoMuted]: !switchCortesFuturos })}>
              {t('desconexion:paginas.mapaCortes.tipoCortes.item3')}
            </span>
          </div>
          <Switch
            checked={switchCortesFuturos}
            onChange={onSwitchCortesFuturosChange}
            color="primary"
            inputProps={{ 'aria-label': 'switch-cortes-futuros-mobile' }}
            data-testid={TEST_ID_SWITCH_FUTUROS_MOBILE}
          />
        </div>
      )}
    </div>
  );

  /**
   * Cierra los paneles del header cuando se abre el detalle del corte.
   */
  useEffect(() => {
    if (modalDetalleAbierto) {
      setPanelMobileAbierto(false);
      setModalReportarAbierto(false);
    }
  }, [modalDetalleAbierto]);

  return (
    <>
      {/**
       * Mobile + sheet: solo logo (espacio superior Figma). Desktop + sheet: se oculta el header.
       */}
      {mostrarHeaderCompleto ? (
        <div className={styles.root}>
          <div className={styles.barra}>
            <Link className={styles.logoLink} to="/" data-testid={TEST_ID_LOGO_HEADER}>
              <img className={styles.logo} src={logo?.urlLogo} alt="saesa" />
            </Link>
            <div className={styles.legendCnt}>{legendItems}</div>
            <Button className={styles.botonReportar} onClick={abrirEstoySinLuz} data-testid={TEST_ID_BOTON_REPORTAR_HEADER}>
              {t('header.botonSinLuz')}
            </Button>
          </div>
        </div>
      ) : (
        <div className={`${styles.root} ${styles.rootSobreSheet}`} data-testid={TEST_ID_LOGO_HEADER_SHEET}>
          <div className={`${styles.barra} ${styles.barraSoloLogo}`}>
            <Link className={styles.logoLink} to="/" data-testid={TEST_ID_LOGO_HEADER}>
              <img className={styles.logo} src={logo?.urlLogo} alt="saesa" />
            </Link>
          </div>
        </div>
      )}

      {mostrarHeaderCompleto && (
        <div className={styles.fabsMobile}>
          <button
            type="button"
            className={styles.fab}
            onClick={abrirModalReportar}
            aria-label={t('header.botonSinLuz')}
            data-testid={TEST_ID_FAB_REPORTAR}
          >
            <img className={styles.iconoHeader} src={flashSlashPurple} alt={CADENA_VACIA} />
          </button>

          {!panelMobileAbierto ? (
            <button
              type="button"
              className={styles.fab}
              ref={fabSimbologiaRef}
              onClick={() => setPanelMobileAbierto(true)}
              aria-label={t('desconexion:paginas.mapaCortes.select.label')}
              aria-expanded={false}
              aria-controls="panel-simbologia-mobile"
              data-testid={TEST_ID_FAB_SIMBOLOGIA}
            >
              <img className={styles.iconoSimbologia} src={legendDots} alt={CADENA_VACIA} />
            </button>
          ) : (
            <div
              className={styles.barraLeyendaMobile}
              id={TEST_ID_PANEL_SIMBOLOGIA}
              data-testid={TEST_ID_PANEL_SIMBOLOGIA}
              role="dialog"
              aria-modal
              aria-label={t('desconexion:paginas.mapaCortes.tipoCortes.title')}
            >
              <button
                type="button"
                className={styles.botonCerrarLeyenda}
                onClick={cerrarPanelMobile}
                aria-label="Cerrar simbología"
                data-testid={TEST_ID_CERRAR_SIMBOLOGIA}
              >
                <img className={styles.iconoHeader} src={closePurple} alt={CADENA_VACIA} />
              </button>
              {legendItemsMobile}
            </div>
          )}
        </div>
      )}

      {modalReportarAbierto && (
        <div className={styles.modalReportarOverlay} data-testid={TEST_ID_MODAL_REPORTAR_OVERLAY}>
          <button
            type="button"
            className={styles.modalReportarBackdrop}
            onClick={cerrarModalReportar}
            aria-label={t('desconexion:paginas.mapaCortes.modalReportar.cerrar')}
            data-testid={TEST_ID_MODAL_REPORTAR_BACKDROP}
          />
          <div
            className={styles.modalReportar}
            role="dialog"
            aria-modal
            aria-labelledby="modal-reportar-titulo"
            data-testid={TEST_ID_MODAL_REPORTAR}
          >
            <button
              type="button"
              className={styles.modalReportarCerrar}
              onClick={cerrarModalReportar}
              aria-label={t('desconexion:paginas.mapaCortes.modalReportar.cerrar')}
              data-testid={TEST_ID_CERRAR_MODAL_REPORTAR}
            >
              <img className={styles.iconoHeader} src={closePurple} alt={CADENA_VACIA} />
            </button>
            <p id="modal-reportar-titulo" className={styles.modalReportarTitulo}>
              {t('desconexion:paginas.mapaCortes.modalReportar.titulo')}
            </p>
            <Button className={styles.modalReportarBoton} onClick={confirmarReportarCorte} data-testid={TEST_ID_CONFIRMAR_REPORTAR}>
              {t('desconexion:paginas.mapaCortes.modalReportar.boton')}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default HeaderMapa;
